"use client";

import React, {
  useEffect,
  useRef,
  useState,
  useTransition,
  useCallback,
} from "react";
import { useQueryState } from "nuqs";
import {
  searchParams,
  type StringSearchParamKey,
} from "@/app/types/search-params";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";

export type SearchQueryInputProps = {
  placeholder?: string;
  className?: string;
  queryKey?: StringSearchParamKey; // defaults to 'q'
};

export default function SearchQueryInput({
  placeholder = "Suchen...",
  className,
  queryKey = "name",
}: SearchQueryInputProps) {
  const [isPending, startTransition] = useTransition();
  const [value, setValue] = useQueryState(
    queryKey,
    searchParams[queryKey].withOptions({ shallow: false, startTransition })
  );
  const [draft, setDraft] = useState<string>(value || "");
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const debouncedSetValue = useCallback(
    (newValue: string) => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        if (newValue !== value) {
          setValue(newValue);
        }
      }, 500);
    },
    [setValue, value]
  );

  useEffect(() => {
    debouncedSetValue(draft);
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [draft, debouncedSetValue]);

  return (
    <div
      className={`flex flex-row items-stretch gap-2.5 ${className ?? ""}`}
      data-pending={isPending ? "" : undefined}
    >
      <Input
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        containerClassName="h-12 overflow-hidden"
        className="inline-block max-lg:h-[3.428571rem] max-lg:w-[114.285714%] max-lg:origin-top-left max-lg:scale-[0.875] max-lg:px-[1.142857rem] lg:h-12 lg:text-sm"
        autoComplete="off"
        name={queryKey}
        type="search"
        id={`search-${queryKey}`}
      />
      <Button className="border-0 py-2" type="button" aria-disabled>
        Suchen
      </Button>
    </div>
  );
}
